import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiAlertCircle, FiLock } from 'react-icons/fi';

import AuthLayout from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import PasswordStrength from '../components/auth/PasswordStrength';
import { resetPassword } from '../api/client';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [values, setValues] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const next = {};
    if (!values.password) next.password = 'Please choose a new password.';
    else if (values.password.length < MIN_PASSWORD_LENGTH)
      next.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (values.confirmPassword !== values.password)
      next.confirmPassword = 'Both passwords need to match.';

    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({ token, ...values });
      // Send them to log in with a notice rather than silently signing them in:
      // proving they can use the new password is worth one extra step.
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been changed. You can log in now.' },
      });
    } catch (err) {
      if (err.body?.errors) setErrors(err.body.errors);
      else setFormError(err.message || 'Could not reset your password.');
    } finally {
      setSubmitting(false);
    }
  };

  const aside = (
    <>
      <h2 className="mm-auth__aside-title">Choose a new password</h2>
      <p className="mm-auth__aside-text">
        Pick something you don&rsquo;t use anywhere else. Longer beats
        complicated &mdash; a short phrase is both stronger and easier to
        remember.
      </p>
    </>
  );

  // A link with no token is unusable; say so instead of failing on submit.
  if (!token) {
    return (
      <AuthLayout aside={aside}>
        <h1 className="mm-auth__title">This link is incomplete</h1>
        <p className="mm-auth__subtitle">
          The reset link looks broken or was only partly copied. Request a fresh
          one and try again.
        </p>
        <Link to="/forgot-password" className="btn btn-primary mm-auth__submit mt-2">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout aside={aside}>
      <h1 className="mm-auth__title">Set a new password</h1>
      <p className="mm-auth__subtitle">
        Once you save this, any other reset links you requested stop working.
      </p>

      <form className="mm-auth__form" onSubmit={handleSubmit} noValidate>
        {formError && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{formError}</span>
          </div>
        )}

        <TextField
          label="New password"
          name="password"
          type="password"
          icon={FiLock}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          required
          value={values.password}
          onChange={handleChange}
          error={errors.password}
        >
          <PasswordStrength password={values.password} />
        </TextField>

        <TextField
          label="Confirm new password"
          name="confirmPassword"
          type="password"
          icon={FiLock}
          placeholder="Type it again"
          autoComplete="new-password"
          required
          value={values.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
        />

        <Button type="submit" className="mm-auth__submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
              Saving…
            </>
          ) : (
            'Save new password'
          )}
        </Button>
      </form>

      <div className="mm-auth__footer">
        <Link to="/login">Back to log in</Link>
      </div>
    </AuthLayout>
  );
}
